import type { Manifest } from '../api/authoring'
import { fieldsSchema } from './manifest'
import { keyAndLabel } from './records'
import { applies, isMachinery, namesTheTree, orderedProperties, resolve, type Schema } from './schema'

// What a node **says**, read off its own stored fields.
//
// Content mode's tree used to draw one row per placement reading the
// type's name — "Section", "Section head", "Text block" — which is the
// right answer to a question a site's owner is not asking. Four
// identically-named rows are four things they have to open one at a time
// to find the one they came to change. So a row shows the words instead,
// and the type's name moves to the side.
//
// **Derived from the fields schema, and from nothing else.** No property
// name is written here and no type is named: which member of a Hero is
// its headline is a fact the manifest carries as declaration order, and
// a table here would be a second declaration of it that a tenant's own
// type could never be in. What this file decides is which *shapes* carry
// words:
//
//   - a string, unless it is a closed vocabulary — an enum is a setting
//     an author picked from a list, not something they wrote;
//   - a list of plain strings, which is the list itself: a check list's
//     items, a marquee's names;
//   - a free-form map's string values, which is the one shape whose
//     members the schema cannot name — a Pattern placement's `props` are
//     typed by the declaration it runs, and they are where its words are.
//
// Everything else is left out: a number, a boolean, an object whose
// properties the type declares (a Container's background is a decision
// about rendering, not a sentence), and above all anything that names
// the tree. A property carrying link ids is an arrangement, and its
// placements are the rows below rather than text on this one. So is a
// field naming a component (a link's page, read as a UUID under its
// label), a field that does not apply under its `x-when`, and a value the
// row has already shown — a record holding its name as a title and a card
// title says it once.
//
// **A concealed field is not left out.** Delivery never serves a
// section's name, and the Preview tab leaves it out for that reason; a
// row is the author's own view, and the name they filed a section under
// is how they find it.
//
// **Nothing here is rendered.** A body is markup — HTML from the rich
// text editor, or markdown — and this takes a reading off it the way
// `component.Label` does in Go: tags dropped, entities decoded, markers
// trimmed, whitespace collapsed. A preview that rendered the markup
// would be this client claiming to be the theme, in a 20px row.

/**
 * How many of a node's values one row is worth.
 *
 * A row is a line and a half, not a document: past three or four values
 * the text is clipped anyway and every row starts to look alike again,
 * which is the failure this exists to fix.
 */
const VALUES = 3

/** How long one value may be before it is cut. */
const LENGTH = 140

/**
 * The words a node holds, in the order its type declares them.
 *
 * Declaration order is a decision somebody made — a section head spells
 * eyebrow, then headline, then lede — so the first value is the one the
 * type puts first, and a caller wanting a single line takes `[0]`.
 *
 * Empty for a node that holds no words at all: a Container, a Repeater
 * whose content is the collection it refers to, a block nobody has
 * written yet. A caller shows the type's own name there, which is what
 * every row used to show.
 */
export function previewOf(
  manifest: Manifest | undefined,
  typeName: string,
  fields: Record<string, unknown>,
): string[] {
  const schema = fieldsSchema(manifest, typeName)
  if (!schema) return []

  // A record's key is its address, which a row is not for.
  const { key } = keyAndLabel(manifest, typeName)
  const said: string[] = []
  for (const property of orderedProperties(schema, schema)) {
    if (said.length >= VALUES) break
    if (key && property.name === key) continue
    const shape = resolve(property.schema, schema)
    // An arrangement is drawn as the rows underneath this one. Reading
    // its link ids out as text would print the model onto exactly the
    // screen that exists to keep it off.
    if (!shape || namesTheTree(shape, schema)) continue
    if (shape['x-component-role'] || isMachinery(shape)) continue
    if (!applies(property.schema, fields)) continue
    for (const text of textsIn(shape, fields[property.name], schema)) {
      if (said.length >= VALUES) break
      if (!said.includes(text)) said.push(text)
    }
  }
  return said
}

function textsIn(shape: Schema, value: unknown, root: Schema): string[] {
  // A vocabulary is a choice among declared values, and a row reading
  // "primary" or "html" is the wire showing through.
  if (shape.enum) return []

  switch (shape.type) {
    case 'string':
      return words(value)
    case 'array': {
      const items = resolve(shape.items, root)
      if (!items || items.type !== 'string' || items.enum) return []
      return Array.isArray(value) ? value.flatMap(words) : []
    }
    case 'object': {
      // Only the map whose members the schema does not name. An object
      // with declared properties is a structured setting — this client
      // has no standing to flatten one into a sentence — and a free-form
      // one is a Pattern's props, where a placement's whole content is.
      if (shape.properties) return []
      if (!value || typeof value !== 'object' || Array.isArray(value)) return []
      return Object.values(value as Record<string, unknown>).flatMap(words)
    }
    default:
      return []
  }
}

/** One value as at most one line of text, or nothing where it says nothing. */
function words(value: unknown): string[] {
  if (typeof value !== 'string') return []
  const text = plain(value)
  if (!text || addressesSomething(text)) return []
  return [text.length > LENGTH ? `${text.slice(0, LENGTH).trimEnd()}…` : text]
}

/**
 * The text inside a value that may be markup, on one line.
 *
 * Tags are dropped rather than parsed: this is a reading of a string,
 * taken while drawing a list, and handing it to a parser would make
 * every row of a long page build a document to throw away. What that
 * costs is exactness on markup nobody writes — an unclosed angle
 * bracket — and what it buys is that a preview can never execute or
 * lay out anything an author pasted.
 *
 * **An inline tag is dropped rather than replaced with a space, and a
 * block-level one is a boundary**, which is the rule the platform reads a
 * rich text label by. The two are the same rule on purpose: a block whose
 * words are a headline is named by the platform in build mode and
 * previewed by this file in content mode, and `a <span>contract</span>.`
 * reading two different ways in the two registers is the disagreement
 * both of them exist to remove. The platform takes the first line and
 * this takes all of them on one, so a boundary is a space here.
 *
 * The leading markers go the way the platform drops them: a markdown
 * body opens with `#` or `>` or a bullet, and none of those is part of
 * what it says.
 */
function plain(value: string): string {
  const text = decode(value.replace(/<\/?\s*([a-z0-9]+)[^>]*>/gi, (_tag, name: string) => (BLOCK.has(name.toLowerCase()) ? ' ' : '')).replace(/<[^>]*>/g, ''))
    .replace(/\s+/g, ' ')
    .trim()
  return text.replace(/^[#>*+\-\s]+/, '')
}

/** The elements an editor opens a new line of text with, as the platform lists them. */
const BLOCK = new Set([
  'address', 'article', 'aside', 'blockquote', 'br', 'dd', 'div', 'dl', 'dt', 'figcaption', 'figure', 'footer',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hr', 'li', 'ol', 'p', 'pre', 'section', 'table', 'td', 'th', 'tr', 'ul',
])

const NAMED: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
}

/**
 * The entities a body carries, as the characters they stand for.
 *
 * Six names and the numeric forms, which is the set an author's own
 * markup actually holds — anything rarer is left as it was written
 * rather than guessed at, and reads as itself in a preview.
 */
function decode(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (whole, body: string) => {
    if (!body.startsWith('#')) return NAMED[body.toLowerCase()] ?? whole
    const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : Number(body.slice(1))
    return Number.isInteger(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : whole
  })
}

/**
 * Whether a value addresses something rather than saying something.
 *
 * A link's `href`, an image's storage key, a content type: all stored as
 * strings, all next to the words in the same document, and none of them
 * worth a row's one line. The test is the value's own shape — no
 * whitespace, and either a path or a scheme — never the property's name,
 * because `href` is what one type happens to call it and the next one
 * calls it `url`.
 *
 * It errs towards showing: a value with a space in it is words, whatever
 * else it looks like.
 *
 * Exported because `ContentPreview` needs the same judgement and must
 * reach it the same way. It found the gap the hard way: a gallery drawn
 * without this rule printed every photo's storage key, content type and
 * checksum under the photo, which is the model showing through on the
 * one screen that exists to keep it hidden.
 */
export function addressesSomething(text: string): boolean {
  if (/\s/.test(text)) return false
  return text.includes('/') || /^(#|[a-z][a-z0-9+.-]*:)/i.test(text)
}
